import React, { useEffect, useMemo, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';
import { StyledStickyContainer } from './StickyNavBar.styles';
import { getNavBarChromeBackgroundColor, StyledNavBarChromeContent } from '../NavBarChrome';
import { useStickyNavBarOffsetData } from '../StickyNavBarOffset';
import { useWeddingCodeBannerOffset } from '@apps/guest/packages/layout-engine/components/WeddingCodeBanner';
import { pxToRem } from '@withjoy/joykit/theme';
import type { NavBarProps } from '../../layouts/LayoutBrannan/components/NavBar';
import { NavBar } from '../../layouts/LayoutBrannan/components/NavBar';
import type { NavbarEventPropsInterface } from '../../layouts/layout.types';

interface StickyNavBarProps
  extends Readonly<Omit<NavBarProps, 'variant'>>,
    Readonly<{
      eventProps: NavbarEventPropsInterface;
      pageColor?: string;
      scrollableContainerRef: React.RefObject<HTMLDivElement>;
    }> {}

export const StickyNavBar: React.FC<StickyNavBarProps> = ({ pageColor, scrollableContainerRef, ...navBarProps }) => {
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const { offset, isVisible: isVisibleFromContext } = useStickyNavBarOffsetData();
  // `offset` is whichever banner the registry elected; the wedding-code banner stacks below it.
  const { weddingBannerHeight } = useWeddingCodeBannerOffset();

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = scrollableContainerRef.current?.scrollTop || 0;
      withWindow(global => setIsScrolledDown(scrollTop > global.innerHeight));
    }, 100);

    const container = scrollableContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }

    return () => undefined;
  }, [scrollableContainerRef]);

  const backgroundColor = useMemo(() => getNavBarChromeBackgroundColor(pageColor), [pageColor]);

  // Combine scroll state with context visibility - navbar shows only when scrolled down AND context allows it
  const shouldShowNavBar = isScrolledDown && isVisibleFromContext;

  const topOffset = offset + weddingBannerHeight;

  return (
    <StyledStickyContainer
      backgroundColor={backgroundColor}
      isVisible={shouldShowNavBar}
      paddingTop={pxToRem(topOffset)}
    >
      <StyledNavBarChromeContent>
        <NavBar
          {...navBarProps}
          variant="inline"
          paddingTop={0}
          expandToFullWidth
        />
      </StyledNavBarChromeContent>
    </StyledStickyContainer>
  );
};
